// Constants
export const FC_NAME = 'Sarah';
export const COMPANY_NAME = 'WRISE';
export const SCRIPTED_AGENT_ID = 'agent_wealth_landscape_forum';
export const EVENT_DATE = 'Saturday, 11 October 2025';
export const EVENT_TIME = '2pm to 5pm';
export const EVENT_VENUE =
  'The Ritz Carlton, Hong Kong International Commerce Centre';

export interface ScriptNode {
  id: string;
  type: 'greeting' | 'statement' | 'question' | 'closing' | 'decision';
  content: string;
  agentMessage: string;
  expectedResponse?: {
    positive?: string[];
    negative?: string[];
    neutral?: string[];
  };
  nextNodes?: {
    positive?: string;
    negative?: string;
    neutral?: string;
    default?: string;
  };
  isEnd?: boolean;
  backgroundColor?: string;
  requiresInput?: boolean;
  waitForResponse?: boolean;
}

export interface Script {
  id: string;
  name: string;
  description: string;
  startNode: string;
  nodes: Record<string, ScriptNode>;
  agentInfo?: {
    name: string;
    voiceId: string;
    avatar?: string;
  };
}

export const wealthLandscapeForumScript: Script = {
  id: 'wealth-landscape-forum',
  name: 'Wealth Landscape Forum Invitation',
  description:
    'Script for inviting clients to the WRISE Wealth Landscape Forum event',
  startNode: '1',
  agentInfo: {
    name: 'Emma',
    voiceId: 'nNMKe8xS6OJl0wUb8Zss',
    avatar:
      'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  },
  nodes: {
    '1': {
      id: '1',
      type: 'greeting',
      content: 'Initial greeting and introduction',
      agentMessage: `So [Jean], we'd like to specially invite you to an exclusive wealth management forum at the Ritz Carlton, which is only open to selected clients. Do you have 5 minutes to speak?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'yeah',
          'yep',
          'sure',
          'okay',
          'ok',
          'go ahead',
          'fine',
          'alright',
          'i have time',
          'i have a few minutes',
          'go on',
          'proceed',
          'continue',
          'absolutely',
          'of course',
          'certainly',
        ],
        negative: [
          'no',
          'nope',
          'busy',
          'not now',
          'later',
          'bad time',
          'not a good time',
          'in a meeting',
          'driving',
          'at work',
          'cant talk',
          'call back',
          'wrong time',
          'not free',
          'occupied',
        ],
      },
      nextNodes: {
        positive: '2',
        negative: '8',
        neutral: '9',
        default: '2',
      },
    },
    '2': {
      id: '2',
      type: 'statement',
      content: 'Introduce the Wealth Landscape Forum',
      agentMessage: `Great! Wrise is delighted to invite you to our inaugural Wealth Landscape Forum on Saturday, 11 October 2025, 2pm to 5pm at The Ritz Carlton, Hong Kong in ICC. This is our first flagship event focusing on 'Connecting Capital, Innovation and Legacy' with insights from top financial experts. Would you be interested in learning more?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'yeah',
          'interested',
          'tell me more',
          'sounds good',
          'what is it',
          'sounds interesting',
          'i want to know more',
          'please continue',
          'go ahead',
          'that sounds nice',
          'i am interested',
          'more details',
          'elaborate',
          'explain more',
        ],
        negative: [
          'no',
          'not interested',
          'no thanks',
          'not really',
          'i dont think so',
          'not for me',
          'pass',
          'skip',
          'not keen',
          'not my thing',
        ],
      },
      nextNodes: {
        positive: '3',
        negative: '6',
        neutral: '3',
        default: '3',
      },
    },
    '3': {
      id: '3',
      type: 'question',
      content: 'Detail the event highlights and speakers',
      agentMessage: `Excellent! This forum covers cutting-edge topics like Digital Assets, Tech and Asian Equity landscapes, Bond Opportunities, and Japan's Economic Rejuvenation. We'll be joined by distinguished speakers including Dennis Chow from Deloitte, Albert Chiu from EFG Bank, and other industry leaders. It's a unique opportunity to gain deeper insights into future wealth trends and mingle first hand with experts. Would you like me to reserve a seat for you and have the team follow up with details?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'yeah',
          'register me',
          'sign me up',
          'sounds great',
          'i want to attend',
          'count me in',
          'book me',
          'reserve my spot',
          'i will attend',
          'put me down',
          'absolutely',
          'definitely',
          'for sure',
        ],
        negative: [
          'no',
          'not sure',
          'need to think',
          'maybe later',
          'cant make it',
          'not available',
          'not really',
          'i dont think so',
          'probably not',
          'unlikely',
          'wont be able to',
          'have other plans',
          'conflicted',
          'saturday is bad',
          'saturday doesnt work',
          'busy that day',
        ],
      },
      nextNodes: {
        positive: '4',
        negative: '7',
        neutral: '7',
        default: '4',
      },
    },
    '4': {
      id: '4',
      type: 'question',
      content: 'Confirm registration and ask about guests',
      agentMessage: `Thank you [Jean]! I'll register you right away. You may also invite 1 additional guest, such as a friend or a family members who might be interested in wealth management insights. Would you like to bring anyone?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'maybe',
          'one guest',
          'two guests',
          'my spouse',
          'my partner',
          'my wife',
          'my husband',
          'a friend',
          'my colleague',
          'someone',
          'possibly',
          'i might',
          'thinking about it',
        ],
        negative: [
          'no',
          'just me',
          'no guests',
          'alone',
          'by myself',
          'no one else',
          'only me',
          'not bringing anyone',
        ],
      },
      nextNodes: {
        default: '5',
      },
    },
    '5': {
      id: '5',
      type: 'closing',
      content: 'Final confirmation and advisor callback offer',
      agentMessage: `Thanks [Jean], you're now signed up for Wrise's Wealth Landscape Forum on Saturday, 11 October 2025. You'll receive a confirmation email with all the details including dress code, venue and parking info. As a final note Stephen your personal advisor would like to offer quick call to catch up on this and see if you have questions. Shall I help you schedule something?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'yeah',
          'register',
          'sign me up',
          'schedule',
          'book it',
          'set it up',
          'arrange it',
          'sounds good',
          'that works',
          'please do',
          'go ahead',
        ],
        negative: [
          'no',
          'hesitant',
          'not sure',
          'no thanks',
          'not needed',
          'not necessary',
          'skip it',
          'not right now',
          'maybe later',
          'not interested',
        ],
      },
      nextNodes: {
        positive: '14',
        negative: '10',
        default: '10',
      },
    },
    '6': {
      id: '6',
      type: 'statement',
      content: 'Handle initial rejection with value proposition',
      agentMessage: `I understand. However, this is a complimentary event for our clients featuring insights you typically wouldn't access elsewhere - including exclusive perspectives on digital assets and emerging investment trends from Asia Pacific's top financial minds. We offer valuable networking, learning opportunities and also refreshments in a compact 3 hours. Would you reconsider joining us?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'okay',
          'maybe',
          'tell me more',
          'sounds interesting',
          'alright',
          'i see',
          'that changes things',
          'worth considering',
          'let me think',
          'you have a point',
          'fair enough',
        ],
        negative: [
          'no',
          'still not interested',
          'no thanks',
          'not convinced',
          'still no',
          'not changing my mind',
          'definitely not',
          'absolutely not',
        ],
      },
      nextNodes: {
        positive: '3',
        negative: '10',
        default: '10',
      },
    },
    '7': {
      id: '7',
      type: 'statement',
      content: 'Address hesitation and offer follow-up',
      agentMessage: `I completely understand the need to consider it. I'd like to highlight this event is complimentary for our clients. Would it be helpful if I send you more detailed information about the speakers and agenda so you can review and then decide? Or I can arrange for Stephen to give you a quick call to share more.`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'send info',
          'email me',
          'more details',
          'send it over',
          'please email',
          'forward the details',
          'share the information',
          'i would like that',
          'that would help',
        ],
        negative: [
          'no',
          'not needed',
          'no thanks',
          'dont bother',
          'not necessary',
          'i dont need it',
          'skip it',
        ],
        neutral: ['rm call', 'stephen call', 'advisor call'],
      },
      nextNodes: {
        positive: '11',
        negative: '10',
        neutral: '14',
        default: '10',
      },
    },
    '8': {
      id: '8',
      type: 'question',
      content: 'Offer callback for busy prospects',
      agentMessage: `I understand you're busy. In short, Wrise is hosting an exclusive wealth management forum with top industry experts, for a limited number of our clients. Would it be better if I call you back at a more convenient time?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'yes',
          'call back',
          'later',
          'another time',
          'better time',
          'different time',
          'call me back',
          'try again',
          'reschedule',
          'good idea',
        ],
        negative: [
          'no',
          'not interested',
          'dont call',
          'dont call back',
          'not at all',
          'never',
          'remove my number',
          'not keen',
        ],
      },
      nextNodes: {
        positive: '12',
        negative: '10',
        default: '10',
      },
    },
    '9': {
      id: '9',
      type: 'closing',
      content: 'Handle hearing issues',
      agentMessage: `Sorry, I'm having some trouble hearing you clearly. If you don't mind, may I try calling back another time to tell you about our exclusive Wealth Landscape Forum, which you are specially invited to? For now, have a great day.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '10': {
      id: '10',
      type: 'closing',
      content: 'Polite closing for disinterested prospects',
      agentMessage: `No problem at all. Thank you for your time today. If you change your mind about joining us please feel free to reach out to us anytime to sign up, by the deadline of 4 October. Have a wonderful day!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '11': {
      id: '11',
      type: 'closing',
      content: 'Promise to send information',
      agentMessage: `Got it! We shall email you further details on the Wealth Landscape Forum including the speaker profiles and the full agenda, and copy Stephen. You may simply reply to register. Thank you for your time!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '12': {
      id: '12',
      type: 'question',
      content: 'Schedule callback time',
      agentMessage: `Great! When would be a better time for me to call you back to discuss this exclusive wealth management event?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '13',
      },
    },
    '13': {
      id: '13',
      type: 'closing',
      content: 'Confirm callback timing',
      agentMessage: `Thanks, I'll call you back then to share more about this event invitation. Appreciate your time, and I wish you a great day!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '14': {
      id: '14',
      type: 'question',
      content: 'Schedule advisor callback',
      agentMessage: `When in the next 3 days would it be convenient for Stephen to drop you a call? For example you can say, Wednesday after 5pm, or Friday lunchtime.`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '15',
      },
    },
    '15': {
      id: '15',
      type: 'closing',
      content: 'Advisor callback confirmation',
      agentMessage: `Thanks, that is noted and I'll share it with him. We look forward to speaking with you again soon. Have an excellent day/evening ahead!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
  },
};
